import React from 'react';
import CollisionRiskTrendChart from './CollisionRiskTrendChart';

interface NewsItem {
  id: number;
  title: string;
  url: string;
  news_site: string;
}

interface WeeklyInsightsSectionProps {
  trendChartData: any;
  isFetchingMetrics: boolean;
  monthlyGpCount: number | null;
  monthlyDebrisTrend: number | null;
  monthlyPayloadsTrend: number | null;
  monthlyKoreanSatellitesCount: number | null;
  isCurrentMonthGpLoading: boolean;
  isLastMonthGpLoading: boolean;
  newsData: NewsItem[] | undefined;
  isNewsLoading: boolean;
}

const WeeklyInsightsSection: React.FC<WeeklyInsightsSectionProps> = ({
  trendChartData,
  isFetchingMetrics,
  monthlyGpCount,
  monthlyDebrisTrend,
  monthlyPayloadsTrend,
  monthlyKoreanSatellitesCount,
  isCurrentMonthGpLoading,
  isLastMonthGpLoading,
  newsData,
  isNewsLoading,
}) => {
  const getTrendColor = (value: number | null) => {
    if (value === null) return 'text-slate-500';
    if (value > 0) return 'text-red-600';
    if (value < 0) return 'text-emerald-600';
    return 'text-slate-500';
  };

  return (
    <div className='bg-white rounded-2xl p-4 w-full max-w-[1400px] shadow-lg'>
      <div className='flex justify-between items-center pb-8 py-4 flex-wrap gap-4'>
        <div className='flex items-center gap-4'>
          <div className='w-12 h-12 rounded-xl flex items-center justify-center text-2xl bg-gradient-to-br from-blue-100 to-blue-200'>
            💡
          </div>
          <p className='text-lg font-bold text-slate-800'>
            Weekly Insights & Trends
          </p>
        </div>
      </div>
      <div className='grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-3'>
        <div className='bg-[#FAFAFA] border border-slate-200 rounded-lg p-4 min-h-[200px]'>
          <p className='text-base font-semibold flex items-center gap-2 mb-4'>
            📈 CA Trend (DCA &lt; 1km)
          </p>
          <div className='relative h-[150px] text-slate-500'>
            {isFetchingMetrics ? (
              'Loading chart...'
            ) : (
              <CollisionRiskTrendChart data={trendChartData} />
            )}
          </div>
        </div>
        <div className='bg-[#FAFAFA] border border-slate-200 rounded-lg px-4 pt-4 min-h-[200px]'>
          <p className='text-base font-semibold flex items-center gap-2'>
            📊 Space Object Status
          </p>
          <div className='flex flex-col gap-2 py-4'>
            <div className='flex justify-between pb-1 border-b border-black/10'>
              <span className='text-sm'>Total Tracked Objects</span>
              <strong className='text-sm text-[#6C6C6C] font-medium'>
                {isCurrentMonthGpLoading
                  ? '...'
                  : monthlyGpCount !== null
                    ? monthlyGpCount.toLocaleString()
                    : 'N/A'}
              </strong>
            </div>
            <div className='flex justify-between pb-1 border-b border-black/10'>
              <span className='text-sm'>New Debris</span>
              <strong
                className={`text-sm font-medium ${getTrendColor(monthlyDebrisTrend)}`}
              >
                {isCurrentMonthGpLoading || isLastMonthGpLoading
                  ? '...'
                  : monthlyDebrisTrend !== null
                    ? `${monthlyDebrisTrend > 0 ? '+' : ''}${monthlyDebrisTrend.toLocaleString()}`
                    : 'N/A'}
              </strong>
            </div>
            <div className='flex justify-between pb-1 border-b border-black/10'>
              <span className='text-sm'>New Payloads</span>
              <strong
                className={`text-sm font-medium ${getTrendColor(monthlyPayloadsTrend)}`}
              >
                {isCurrentMonthGpLoading || isLastMonthGpLoading
                  ? '...'
                  : monthlyPayloadsTrend !== null
                    ? `${monthlyPayloadsTrend > 0 ? '+' : ''}${monthlyPayloadsTrend.toLocaleString()}`
                    : 'N/A'}
              </strong>
            </div>
            <div className='flex justify-between pb-1'>
              <span className='text-sm'>Number of Korean Sats.</span>
              <strong className='text-sm text-slate-600 font-medium'>
                {isCurrentMonthGpLoading
                  ? '...'
                  : monthlyKoreanSatellitesCount !== null
                    ? monthlyKoreanSatellitesCount.toLocaleString()
                    : 'N/A'}
              </strong>
            </div>
          </div>
        </div>
        <div className='bg-[#FAFAFA] border border-slate-200 rounded-lg px-4 pt-4 min-h-[200px]'>
          <p className='text-base font-semibold flex items-center gap-2'>
            📰 Key News
          </p>
          <div className='flex-1'>
            {isNewsLoading ? (
              <p>Loading news...</p>
            ) : (
              newsData?.map((newsItem, index) => (
                <div
                  key={newsItem.id}
                  className={`py-4 ${index < (newsData?.length || 0) - 1 ? 'border-b border-[#E4E8EB]' : ''}`}
                >
                  <a
                    href={newsItem.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='no-underline text-[#6c6c6c] font-medium text-sm hover:underline'
                  >
                    <div className='flex flex-col gap-2'>
                      <div className='flex items-start'>{newsItem.title}</div>
                      <div className='flex items-end justify-end'>
                        <span className='text-xs text-[#a7a7a7] font-medium'>
                          {newsItem.news_site}
                        </span>
                      </div>
                    </div>
                  </a>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyInsightsSection;
